// Seletor de local de entrega da barra superior.
//
// Era um placeholder com "Enviar para: Tubarão (88701-826)" em código, comentado em
// Header/index.js. Agora é o gatilho do contexto de entrega, renderizado dentro do
// LeftBlock de TopbarIdentidadeVisual — que é a barra onde mora a mensagem central que ele
// não pode deslocar quando cresce.
//
// Rótulo de endereço é bairro e cidade. O modelo não tem apelido, e nome_contato identifica
// pessoa, não lugar. Ver docs/contexto-entrega/rotulo-endereco.md.

import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FiMapPin, FiChevronDown } from "react-icons/fi";

import {
  Status,
  Cobertura,
  Creators as DeliveryContextActions,
  selectRotuloLocal
} from "../../store/ducks/deliveryContext";
import DeliveryLocationPanel from "../DeliveryLocationPanel";
import { formatCEP } from "../../util/formatCEP";
import {
  Gatilho,
  Conteudo,
  Texto,
  Selo,
  Shimmer,
  ShimmerCirculo,
  ShimmerBarra
} from "./styles";

const LIMITE_TEXTO = 28;

function truncar(texto) {
  if (!texto) return texto;
  return texto.length > LIMITE_TEXTO
    ? `${texto.slice(0, LIMITE_TEXTO - 1)}…`
    : texto;
}

// Quatro estados de conteúdo — vazio, localidade, sem cobertura e carregando — mais falha e
// localidade incompleta, que reaproveitam a moldura de atenção com texto próprio.
//
// A distinção importa: LOCALIDADE_INCOMPLETA é CEP que resolveu sem bairro, e pede o
// endereço completo; nunca pode ser lido como ausência de entrega. FALHA é erro de rede ou
// de servidor, e a API responde sempre 200 quando não há cobertura.
export function describeState({ status, cobertura, cep, rotulo }) {
  const cepFormatado = formatCEP(cep);

  if (status === Status.RESOLVENDO) {
    return { variante: "carregando" };
  }

  if (status === Status.FALHA) {
    return {
      variante: "atencao",
      texto: "Não foi possível consultar",
      selo: "Tentar de novo"
    };
  }

  if (status === Status.NAO_ATENDIDO) {
    if (cobertura === Cobertura.LOCALIDADE_INCOMPLETA) {
      return {
        variante: "atencao",
        texto: "Informe o endereço completo",
        selo: "Atenção"
      };
    }
    return {
      variante: "atencao",
      texto: cepFormatado ? `Sem entrega em ${cepFormatado}` : "Sem entrega",
      selo: "Atenção"
    };
  }

  if (status === Status.ATENDIDO) {
    return {
      variante: "definido",
      texto: truncar(rotulo || (cepFormatado ? `Entregar em ${cepFormatado}` : "Entregar"))
    };
  }

  return { variante: "vazio", texto: "Informe seu CEP" };
}

export default function HeaderLocationDelivery() {
  const dispatch = useDispatch();
  const [aberto, setAberto] = useState(false);
  // O painel vive em portal no body e se posiciona a partir do retangulo do gatilho.
  const gatilhoRef = useRef(null);
  const [destacado, setDestacado] = useState(false);

  const { status, cobertura, cep, destaqueEm } = useSelector(
    (state) => state.deliveryContext
  );
  const rotulo = useSelector(selectRotuloLocal);

  const estado = describeState({ status, cobertura, cep, rotulo });

  // Destaque temporario quando a alteracao parte do card da pagina de produto: a mudanca
  // acontece fora do campo de visao do cliente. 500ms de transicao, mantido por 600ms.
  useEffect(() => {
    if (!destaqueEm) return undefined;

    setDestacado(true);
    const timer = setTimeout(() => setDestacado(false), 600);
    return () => clearTimeout(timer);
  }, [destaqueEm]);

  function abrirPainel() {
    // Erro de digitação anterior não pode reaparecer quando o painel volta a abrir.
    dispatch(DeliveryContextActions.clearCepError());
    setAberto((atual) => !atual);
  }

  // O shimmer fica DENTRO do gatilho, nunca no lugar dele.
  //
  // Aplicar um CEP leva o status a RESOLVENDO na mesma hora. Com um retorno antecipado aqui,
  // o gatilho desmontaria no meio da consulta, levando junto o painel aberto e invalidando o
  // anchorRef de que a ancoragem depende.
  const carregando = estado.variante === "carregando";

  return (
    <>
      <Gatilho
        ref={gatilhoRef}
        type="button"
        variante={estado.variante}
        destacado={destacado}
        onClick={abrirPainel}
        aria-haspopup="dialog"
        aria-expanded={aberto}
        aria-controls={aberto ? "painel-entrega" : undefined}
      >
        {carregando ? (
          <Shimmer aria-live="polite" aria-label="Consultando entrega">
            <ShimmerCirculo />
            <ShimmerBarra />
          </Shimmer>
        ) : (
          <Conteudo>
            <FiMapPin size={16} aria-hidden="true" />
            <Texto variante={estado.variante}>{estado.texto}</Texto>
            {estado.selo && <Selo>{estado.selo}</Selo>}
            {estado.variante === "definido" && (
              <FiChevronDown size={14} aria-hidden="true" />
            )}
          </Conteudo>
        )}
      </Gatilho>

      {aberto && (
        <DeliveryLocationPanel
          anchorRef={gatilhoRef}
          align="left"
          onClose={() => setAberto(false)}
        />
      )}
    </>
  );
}
